import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HammerModule } from '@angular/platform-browser';

import { FlexLayoutModule } from 'ngx-flexible-layout';
import { GoogleMapsModule } from "@angular/google-maps";
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from './material/material.module';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ToastrModule } from 'ngx-toastr';
import { NgxMatDatetimePickerModule, NgxMatNativeDateModule, NgxMatTimepickerModule, NgxMatDateAdapter, NgxMatDateFormats, NGX_MAT_DATE_FORMATS } from '@angular-material-components/datetime-picker';
import { NgxMatMomentModule } from '@angular-material-components/moment-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { AppDatetimeAdapter, APP_DATETIME_FORMATS } from './datetime-adapter';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';

import { AngularFireModule } from '@angular/fire/compat';

import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireAuthGuardModule } from '@angular/fire/compat/auth-guard';

import { SocialLoginModule, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { GoogleLoginProvider } from '@abacritt/angularx-social-login';
import { ApiModule, Configuration, ConfigurationParameters } from './api-client-module/';

import { QRCodeModule } from 'angularx-qrcode';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { HeaderComponent } from './common/header/header.component';
import { SignInComponent } from './common/sign-in/sign-in.component';
import { SignUpComponent } from './common/sign-up/sign-up.component';
import { PasswordResetMailComponent } from './common/password-reset-mail/password-reset-mail.component';
import { PasswordResetComponent } from './common/password-reset/password-reset.component';

import { TopComponent } from './top/top.component';
import { ProjectComponent } from './project/project.component';
import { TypeOfKeypadPipe } from './common/pipes/type-of-keypad.pipe';
import { UserComponent } from './user/user.component';
import { FacilityAttributeComponent } from './master/facility-attribute/facility-attribute.component';
import { ExhibitAttributeComponent } from './master/exhibit-attribute/exhibit-attribute.component';
import { ExhibitDetailAttributeComponent } from './master/exhibit-detail-attribute/exhibit-detail-attribute.component';
import { ReservationFacilityAttributeComponent } from './master/reservation-facility-attribute/reservation-facility-attribute.component';
import { UploadedFilesAttributeComponent } from './master/uploaded-files-attribute/uploaded-files-attribute.component';
import { FacilityComponent } from './facilities/facility.component';
import { MenuDispMapRangePipe } from './common/pipes/menu-disp-map-range.pipe';
import { TypeOfMapPipe } from './common/pipes/type-of-map.pipe';
import { ExhibitComponent } from './exhibits/exhibit.component';
import { ExhibitDetailComponent } from './exhibit-details/exhibit-detail.component';
import { ExhibitRankingsComponent } from './exhibit-rankings/exhibit-rankings.component';
import { VoiceFileDownloadComponent } from './voice-file-download/voice-file-download.component';
import { ReservationFacilityComponent } from './reservation-facilities/reservation-facility.component';
import { ReservationFacilitySpecialComponent } from './reservation-facilities/reservation-facility-special/reservation-facility-special.component';
import { ReservationComponent } from './reservations/reservation.component';
import { NumberDayPipe } from './common/pipes/number-day.pipe';
import { UseFunctionPipe } from './common/pipes/use-function.pipe';
import { ReservationStatusPipe } from './common/pipes/reservation-status.pipe';
import { MailSendStatusPipe } from './common/pipes/mail-send-status.pipe';
import { GenderPipe } from './common/pipes/gender.pipe';

import { TopDialogComponent } from './top/top-dialog/top-dialog.component';
import { ProjectDialogComponent } from './project/project-dialog/project-dialog.component';
import { FacilityAttributeDialogComponent } from './master/facility-attribute/facility-attribute-dialog/facility-attribute-dialog.component';
import { ExhibitAttributeDialogComponent } from './master/exhibit-attribute/exhibit-attribute-dialog/exhibit-attribute-dialog.component';
import { ExhibitDetailAttributeDialogComponent } from './master/exhibit-detail-attribute/exhibit-detail-attribute-dialog/exhibit-detail-attribute-dialog.component';
import { ReservationFacilityAttributeDialogComponent } from './master/reservation-facility-attribute/reservation-facility-attribute-dialog/reservation-facility-attribute-dialog.component';
import { UploadedFilesAttributeDialogComponent } from './master/uploaded-files-attribute/uploaded-files-attribute-dialog/uploaded-files-attribute-dialog.component';
import { FacilityDialogComponent } from './facilities/facility-dialog/facility-dialog.component';
import { ExhibitDialogComponent } from './exhibits/exhibit-dialog/exhibit-dialog.component';
import { ExhibitDetailDialogComponent } from './exhibit-details/exhibit-detail-dialog/exhibit-detail-dialog.component';
import { SelectExhibitDetailsDialogComponent } from './exhibits/select-exhibit-details-dialog/select-exhibit-details-dialog.component';
import { SelectReservationFacilitiesDialogComponent } from './exhibits/select-reservation-facilities-dialog/select-reservation-facilities-dialog.component';
import { SelectUploadedFileDialogComponent } from './select-uploaded-file-dialog/select-uploaded-file-dialog.component';
import { SelectExhibitsDialogComponent } from './floors/select-exhibits-dialog/select-exhibits-dialog.component';
import { ReservationFacilityDialogComponent } from './reservation-facilities/reservation-facility-dialog/reservation-facility-dialog.component';
import { ReservationFacilitySpecialDialogComponent } from './reservation-facilities/reservation-facility-special/reservation-facility-special-dialog/reservation-facility-special-dialog.component';
import { ReservationQuestionsComponent } from './reservation-questions/reservation-questions.component';
import { ReservationQuestionDialogComponent } from './reservation-questions/reservation-question-dialog/reservation-question-dialog.component';
import { SelectReservationQuestionsDialogComponent } from './reservation-facilities/select-reservation-questions-dialog/select-reservation-questions-dialog.component';
import { ReservationFacilityReservationDialogComponent } from './reservation-facilities/reservation-facility-reservation-dialog/reservation-facility-reservation-dialog.component';
import { ReservationDialogComponent } from './reservations/reservation-dialog/reservation-dialog.component';
import { SelectReservationDialogComponent } from './reservations/select-reservation-dialog/select-reservation-dialog.component';
import { SelectReservationReservationFacilitiesDialogComponent } from './reservations/select-reservation-reservation-facilities-dialog/select-reservation-reservation-facilities-dialog.component';
import { ConfirmReservationDialogComponent } from './reservations/confirm-reservation-dialog/confirm-reservation-dialog.component';
import { ConfirmReservationDetailDialogComponent } from './reservations/confirm-reservation-detail-dialog/confirm-reservation-detail-dialog.component';
import { ReservationDownloadComponent } from './reservation-download/reservation-download.component';
import { ReservationDownloadDialogComponent } from './reservation-download/reservation-download-dialog/reservation-download-dialog.component';
import { FacilityUserComponent } from './facility-users/facility-user.component';
import { FacilityUserDialogComponent } from './facility-users/facility-user-dialog/facility-user-dialog.component';
import { SelectFacilitiesDialogComponent } from './facility-users/select-facilities-dialog/select-facilities-dialog.component';
import { PurchaseContentDailyComponent } from './purchase-content-dailies/purchase-content-daily.component';
import { SelectPurchaseContentDailyDialogComponent } from './purchase-content-dailies/select-purchase-content-daily-dialog/select-purchase-content-daily-dialog.component';
import { SelectHallsDialogComponent } from './purchase-content-dailies/select-halls-dialog/select-halls-dialog.component';
import { EntranceExitUserComponent } from './entrance-exit-users/entrance-exit-user.component';
import { EntranceExitUserDialogComponent } from './entrance-exit-users/entrance-exit-user-dialog/entrance-exit-user-dialog.component';
import { EntranceExitEventComponent } from './entrance-exit-event/entrance-exit-event.component';
import { SelectEntranceExitQuestionsDialogComponent } from './entrance-exit-event/select-entrance-exit-questions-dialog/select-entrance-exit-questions-dialog.component';
import { SelectEntranceExitEventFloorsDialogComponent } from './entrance-exit-event/select-entrance-exit-event-floors-dialog/select-entrance-exit-event-floors-dialog.component';
import { EntranceExitQuestionComponent } from './entrance-exit-questions/entrance-exit-question.component';
import { EntranceExitQuestionDialogComponent } from './entrance-exit-questions/entrance-exit-question-dialog/entrance-exit-question-dialog.component';
import { EntranceExitEntryComponent } from './entrance-exit-entries/entrance-exit-entry.component';
import { EntranceExitEntryDialogComponent } from './entrance-exit-entries/entrance-exit-entry-dialog/entrance-exit-entry-dialog.component';
import { SelectEntranceExitEntryDialogComponent } from './entrance-exit-entries/select-entrance-exit-entry-dialog/select-entrance-exit-entry-dialog.component';
import { EntranceExitTransitDialogComponent } from './entrance-exit-entries/entrance-exit-transit-dialog/entrance-exit-transit-dialog.component';
import { EntranceExitTransitDailyComponent } from './entrance-exit-transit-dailies/entrance-exit-transit-daily.component';
import { EntranceExitTransitDailyDialogComponent } from './entrance-exit-transit-dailies/entrance-exit-transit-daily-dialog/entrance-exit-transit-daily-dialog.component';
import { SelectEntranceExitTransitDailyDialogComponent } from './entrance-exit-transit-dailies/select-entrance-exit-transit-daily-dialog/select-entrance-exit-transit-daily-dialog.component';
import { EntranceExitExhibitTransitDownloadComponent } from './entrance-exit-exhibit-transit-download/entrance-exit-exhibit-transit-download.component';
import { EntranceExitExhibitTransitDownloadDialogComponent } from './entrance-exit-exhibit-transit-download/entrance-exit-exhibit-transit-download-dialog/entrance-exit-exhibit-transit-download-dialog.component';
import { SplatsComponent } from './splats/splats.component';

import { DropzoneDirective } from './dropzone.directive';
import { UploaderComponent } from './project/uploader/uploader.component';
import { UploaderDLComponent } from './project/uploader-dl/uploader-dl.component';
import { UploadTaskComponent } from './project/upload-task/upload-task.component';
import { MessageDialogComponent } from './common/message-dialog/message-dialog.component';
import { UserDialogComponent } from './user/user-dialog/user-dialog.component';
import { TypeOfUserPipe } from './common/pipes/type-of-user.pipe';
import { SelectLogoFileDialogComponent } from './select-logo-file-dialog/select-logo-file-dialog.component';
import { DocPipe } from './common/pipes/doc.pipe';
import { SelectLanguagesDialogComponent } from './top/top-dialog/select-languages-dialog/select-languages-dialog.component';
import { MultiNamePipe } from './common/pipes/multi-name.pipe';
import { MultiNameDialogComponent } from './common/multi-name-dialog/multi-name-dialog.component';
import { MultiDescDialogComponent } from './common/multi-desc-dialog/multi-desc-dialog.component';
import { MultiDescPipe } from './common/pipes/multi-desc.pipe';
import { MultiAddressDialogComponent } from './common/multi-address-dialog/multi-address-dialog.component';
import { MultiAddressPipe } from './common/pipes/multi-address.pipe';
import { MultiInquiryDialogComponent } from './common/multi-inquiry-dialog/multi-inquiry-dialog.component';
import { MultiPolicyDialogComponent } from './common/multi-policy-dialog/multi-policy-dialog.component';
import { MultiCopyrightDialogComponent } from './common/multi-copyright-dialog/multi-copyright-dialog.component';
import { MultiFaqDialogComponent } from './common/multi-faq-dialog/multi-faq-dialog.component';
import { MultiInquiryPipe } from './common/pipes/multi-inquiry.pipe';
import { MultiPolicyPipe } from './common/pipes/multi-policy.pipe';
import { MultiCopyrightPipe } from './common/pipes/multi-copyright.pipe';
import { MultiFaqPipe } from './common/pipes/multi-faq.pipe';
import { FloorComponent } from './floors/floor.component';
import { FloorDialogComponent } from './floors/floor-dialog/floor-dialog.component';
import { SelectFloorsDialogComponent } from './facilities/select-floors-dialog/select-floors-dialog.component';
import { MultiFloorDialogComponent } from './common/multi-floor-dialog/multi-floor-dialog.component';
import { HallComponent } from './halls/hall.component';
import { HallDialogComponent } from './halls/hall-dialog/hall-dialog.component';
import { TypeOfMenuPipe } from './common/pipes/type-of-menu.pipe';
import { TypeOfVoiceControlPipe } from './common/pipes/type-of-voice-control.pipe';
import { TypeOfPinTapVoiceControlPipe } from './common/pipes/type-of-pin-tap-voice-control.pipe';
import { DocsByCondPipe } from './common/pipes/docs-by-cond.pipe';
import { GeneralTextComponent } from './general-text/general-text.component';
import { GeneralTextDialogComponent } from './general-text/general-text-dialog/general-text-dialog.component';
import { GeneralTextPipe } from './common/pipes/general-text.pipe';
import { MultiGeneralTextDialogComponent } from './common/multi-general-text-dialog/multi-general-text-dialog.component';
import { LanguageComponent } from './languages/language.component';
import { LanguageDialogComponent } from './languages/language-dialog/language-dialog.component';
import { SelectLangFileDialogComponent } from './select-lang-file-dialog/select-lang-file-dialog.component';
import { SelectVoiceFileDialogComponent } from './select-voice-file-dialog/select-voice-file-dialog.component';
import { SelectFaviconFileDialogComponent } from './select-favicon-file-dialog/select-favicon-file-dialog.component';
import { SelectAngularIconDialogComponent } from './select-angular-icon-dialog/select-angular-icon-dialog.component';
import { ReflectDataDialogComponent } from './facilities/reflect-data-dialog/reflect-data-dialog.component';
import { NoticeComponent } from './notices/notice.component';
import { NoticeManagementComponent } from './notice-management/notice-management.component';
import { NoticeManagementDialogComponent } from './notice-management/notice-management-dialog/notice-management-dialog.component';
import { SelectProjectDialogComponent } from './select-project-dialog/select-project-dialog.component';
import { MultiAppnameDialogComponent } from './common/multi-appname-dialog/multi-appname-dialog.component';
import { ManualComponent } from './manuals/manual.component';
import { ManualIndexComponent } from './manuals/manual-index/manual-index.component';
import { ManualTopComponent } from './manuals/manual-top/manual-top.component';
import { ManualTopDialogComponent } from './manuals/manual-top-dialog/manual-top-dialog.component';
import { ManualProjectComponent } from './manuals/manual-project/manual-project.component';
import { ManualProjectDialogComponent } from './manuals/manual-project-dialog/manual-project-dialog.component';
import { ManualUserComponent } from './manuals/manual-user/manual-user.component';
import { ManualUserDialogComponent } from './manuals/manual-user-dialog/manual-user-dialog.component';
import { ManualUploaderComponent } from './manuals/manual-uploader/manual-uploader.component';
import { ManualUploaderDLComponent } from './manuals/manual-uploader-dl/manual-uploader-dl.component';
import { ManualFacilityComponent } from './manuals/manual-facility/manual-facility.component';
import { ManualFacilityDialogComponent } from './manuals/manual-facility-dialog/manual-facility-dialog.component';
import { ManualHallComponent } from './manuals/manual-hall/manual-hall.component';
import { ManualHallDialogComponent } from './manuals/manual-hall-dialog/manual-hall-dialog.component';
import { ManualFloorComponent } from './manuals/manual-floor/manual-floor.component';
import { ManualFloorDialogComponent } from './manuals/manual-floor-dialog/manual-floor-dialog.component';
import { ManualExhibitComponent } from './manuals/manual-exhibit/manual-exhibit.component';
import { ManualExhibitDialogComponent } from './manuals/manual-exhibit-dialog/manual-exhibit-dialog.component';
import { ManualExhibitDetailComponent } from './manuals/manual-exhibit-detail/manual-exhibit-detail.component';
import { ManualExhibitDetailDialogComponent } from './manuals/manual-exhibit-detail-dialog/manual-exhibit-detail-dialog.component';
import { ManualReservationFacilityComponent } from './manuals/manual-reservation-facility/manual-reservation-facility.component';
import { ManualReservationFacilityDialogComponent } from './manuals/manual-reservation-facility-dialog/manual-reservation-facility-dialog.component';
import { ManualReservationFacilityReservationComponent } from './manuals/manual-reservation-facility-reservation/manual-reservation-facility-reservation.component';
import { ManualReservationFacilitySpecialComponent } from './manuals/manual-reservation-facility-special/manual-reservation-facility-special.component';
import { ManualReservationFacilitySpecialDialogComponent } from './manuals/manual-reservation-facility-special-dialog/manual-reservation-facility-special-dialog.component';
import { ManualReservationComponent } from './manuals/manual-reservation/manual-reservation.component';
import { ManualReservationQuestionComponent } from './manuals/manual-reservation-question/manual-reservation-question.component';
import { ManualReservationQuestionDialogComponent } from './manuals/manual-reservation-question-dialog/manual-reservation-question-dialog.component';
import { ManualReservationDownloadComponent } from './manuals/manual-reservation-download/manual-reservation-download.component';
import { ManualReservationDownloadDialogComponent } from './manuals/manual-reservation-download-dialog/manual-reservation-download-dialog.component';
import { ManualReservationDialogComponent } from './manuals/manual-reservation-dialog/manual-reservation-dialog.component';
import { ManualUserInputTitleDialogComponent } from './manuals/manual-user-input-title-dialog/manual-user-input-title-dialog.component';
import { ManualSendMailTitleDialogComponent } from './manuals/manual-send-mail-title-dialog/manual-send-mail-title-dialog.component';
import { ManualSendMailMessageDialogComponent } from './manuals/manual-send-mail-message-dialog/manual-send-mail-message-dialog.component';
import { ManualFacilityAttributeComponent } from './manuals/manual-facility-attribute/manual-facility-attribute.component';
import { ManualExhibitAttributeComponent } from './manuals/manual-exhibit-attribute/manual-exhibit-attribute.component';
import { ManualExhibitDetailAttributeComponent } from './manuals/manual-exhibit-detail-attribute/manual-exhibit-detail-attribute.component';
import { ManualReservationFacilityAttributeComponent } from './manuals/manual-reservation-facility-attribute/manual-reservation-facility-attribute.component';
import { ManualFacilityAttributeDialogComponent } from './manuals/manual-facility-attribute-dialog/manual-facility-attribute-dialog.component';
import { ManualExhibitAttributeDialogComponent } from './manuals/manual-exhibit-attribute-dialog/manual-exhibit-attribute-dialog.component';
import { ManualExhibitDetailAttributeDialogComponent } from './manuals/manual-exhibit-detail-attribute-dialog/manual-exhibit-detail-attribute-dialog.component';
import { ManualReservationFacilityAttributeDialogComponent } from './manuals/manual-reservation-facility-attribute-dialog/manual-reservation-facility-attribute-dialog.component';
import { ManualAppnameDialogComponent } from './manuals/manual-appname-dialog/manual-appname-dialog.component';
import { ManualNameDialogComponent } from './manuals/manual-name-dialog/manual-name-dialog.component';
import { ManualDescDialogComponent } from './manuals/manual-desc-dialog/manual-desc-dialog.component';
import { ManualAddressDialogComponent } from './manuals/manual-address-dialog/manual-address-dialog.component';
import { ManualFaqDialogComponent } from './manuals/manual-faq-dialog/manual-faq-dialog.component';
import { ManualPolicyDialogComponent } from './manuals/manual-policy-dialog/manual-policy-dialog.component';
import { ManualCopyrightDialogComponent } from './manuals/manual-copyright-dialog/manual-copyright-dialog.component';
import { ManualNoticeComponent } from './manuals/manual-notice/manual-notice.component';
import { ManualReflectDataComponent } from './manuals/manual-reflect-data/manual-reflect-data.component';
import { ManualSelectReservationDialogComponent } from './manuals/manual-select-reservation-dialog/manual-select-reservation-dialog.component';
import { ManualConfirmReservationDialogComponent } from './manuals/manual-confirm-reservation-dialog/manual-confirm-reservation-dialog.component';
import { ManualConfirmReservationDetailDialogComponent } from './manuals/manual-confirm-reservation-detail-dialog/manual-confirm-reservation-detail-dialog.component';
import { ManualVoiceFileDownloadComponent } from './manuals/manual-voice-file-download/manual-voice-file-download.component';
import { ProgressSpinnerComponent } from './common/progress-spinner/progress-spinner.component';
import { MultiYoutubeDialogComponent } from './common/multi-youtube-dialog/multi-youtube-dialog.component';
import { MultiExternallinkDialogComponent } from './common/multi-externallink-dialog/multi-externallink-dialog.component';
import { MultiCodePipe } from './common/pipes/multi-code.pipe';
import { ManualYoutubeDialogComponent } from './manuals/manual-youtube-dialog/manual-youtube-dialog.component';
import { QrcodeComponent } from './qrcode/qrcode.component';
import { QrcodeResultComponent } from './qrcode/qrcode-result/qrcode-result.component';
import { URLResultComponent } from './qrcode/url-result/url-result.component';
import { SelectedLanguagesDialogComponent } from './qrcode/selected-languages-dialog/selected-languages-dialog.component';
import { ManualQrcodeComponent } from './manuals/manual-qrcode/manual-qrcode.component';
import { ManualQrcodeResultComponent } from './manuals/manual-qrcode-result/manual-qrcode-result.component';
import { ManualURLResultComponent } from './manuals/manual-url-result/manual-url-result.component';
import { MultiQnameDialogComponent } from './common/multi-qname-dialog/multi-qname-dialog.component';
import { MultiQcompDialogComponent } from './common/multi-qcomp-dialog/multi-qcomp-dialog.component';
import { MultiQitemDialogComponent } from './common/multi-qitem-dialog/multi-qitem-dialog.component';
import { QuestionsComponent } from './questions/questions.component';
import { QuestionDialogComponent } from './questions/question-dialog/question-dialog.component';
import { QuestionSortDialogComponent } from './questions/question-sort-dialog/question-sort-dialog.component';
import { ManualQuestionComponent } from './manuals/manual-question/manual-question.component';
import { ManualQuestionDialogComponent } from './manuals/manual-question-dialog/manual-question-dialog.component';
import { ManualQuestionSortDialogComponent } from './manuals/manual-question-sort-dialog/manual-question-sort-dialog.component';
import { ManualQnameDialogComponent } from './manuals/manual-qname-dialog/manual-qname-dialog.component';
import { ManualQcompDialogComponent } from './manuals/manual-qcomp-dialog/manual-qcomp-dialog.component';
import { ManualQitemDialogComponent } from './manuals/manual-qitem-dialog/manual-qitem-dialog.component';
import { EnqueteComponent } from './enquete/enquete.component';
import { EnqueteDialogComponent } from './enquete/enquete-dialog/enquete-dialog.component';
import { ManualEnqueteComponent } from './manuals/manual-enquete/manual-enquete.component';
import { ManualEnqueteDialogComponent } from './manuals/manual-enquete-dialog/manual-enquete-dialog.component';
import { MultiQnamePipe } from './common/pipes/multi-qname.pipe';
import { MultiQcompPipe } from './common/pipes/multi-qcomp.pipe';
import { MultiQitemPipe } from './common/pipes/multi-qitem.pipe';
import { ManualUrlDialogComponent } from './manuals/manual-url-dialog/manual-url-dialog.component';
import { ManualExhibitRankingComponent } from './manuals/manual-exhibit-ranking/manual-exhibit-ranking.component';
import { BoardThreadComponent } from './board-thread/board-thread.component';
import { BoardCommentComponent } from './board-comment/board-comment.component';
import { BoardThreadDialogComponent } from './board-thread/board-thread-dialog/board-thread-dialog.component';
import { CutterPipe } from './common/pipes/cutter.pipe';
import { BoardCommentDialogComponent } from './board-comment/board-comment-dialog/board-comment-dialog.component';
import { ManualBoardThreadComponent } from './manuals/manual-board-thread/manual-board-thread.component';
import { ManualBoardThreadDialogComponent } from './manuals/manual-board-thread-dialog/manual-board-thread-dialog.component';
import { ManualBoardCommentComponent } from './manuals/manual-board-comment/manual-board-comment.component';
import { ManualBoardCommentDialogComponent } from './manuals/manual-board-comment-dialog/manual-board-comment-dialog.component';
import { SheltersComponent } from './shelters/shelters.component';
import { ShelterDialogComponent } from './shelters/shelter-dialog/shelter-dialog.component';
import { ManualShelterComponent } from './manuals/manual-shelter/manual-shelter.component';
import { ManualShelterDialogComponent } from './manuals/manual-shelter-dialog/manual-shelter-dialog.component';
import { QuestSetComponent } from './quest-set/quest-set.component';
import { QuestSetDialogComponent } from './quest-set/quest-set-dialog/quest-set-dialog.component';
import { SelectQuestionsDialogComponent } from './quest-set/select-questions-dialog/select-questions-dialog.component';
import { ManualQuestSetComponent } from './manuals/manual-quest-set/manual-quest-set.component';
import { ManualQuestSetDialogComponent } from './manuals/manual-quest-set-dialog/manual-quest-set-dialog.component';
import { ManualFacilityUserComponent } from './manuals/manual-facility-user/manual-facility-user.component';
import { ManualFacilityUserDialogComponent } from './manuals/manual-facility-user-dialog/manual-facility-user-dialog.component';
import { MethodOfReservationPipe } from './common/pipes/method-of-reservation.pipe';
import { MethodOfLotteryPipe } from './common/pipes/method-of-lottery.pipe';
import { UnitOfReservationPipe } from './common/pipes/unit-of-reservation.pipe';
import { MultiRitemDialogComponent } from './common/multi-ritem-dialog/multi-ritem-dialog.component';
import { MultiRitemPipe } from './common/pipes/multi-ritem.pipe';
import { TypeOfReservationSpecialDatePipe } from './common/pipes/type-of-reservation-special-date.pipe';
import { MultiUserInputTitleDialogComponent } from './common/multi-user-input-title-dialog/multi-user-input-title-dialog.component';
import { MultiUserInputTitlePipe } from './common/pipes/multi-user-input-title.pipe';
import { MultiSendMailTitleDialogComponent } from './common/multi-send-mail-title-dialog/multi-send-mail-title-dialog.component';
import { MultiSendMailTitlePipe } from './common/pipes/multi-send-mail-title.pipe';
import { MultiSendMailMessageDialogComponent } from './common/multi-send-mail-message-dialog/multi-send-mail-message-dialog.component';
import { MultiSendMailMessagePipe } from './common/pipes/multi-send-mail-message.pipe';
import { MultiMailTitleDialogComponent } from './common/multi-mail-title-dialog/multi-mail-title-dialog.component';
import { MultiMailTitlePipe } from './common/pipes/multi-mail-title.pipe';
import { MultiMailMessageDialogComponent } from './common/multi-mail-message-dialog/multi-mail-message-dialog.component';
import { MultiMailMessagePipe } from './common/pipes/multi-mail-message.pipe';
import { MultiStringMessageDialogComponent } from './common/multi-string-message-dialog/multi-string-message-dialog.component';
import { MultiStringMessagePipe } from './common/pipes/multi-string-message.pipe';
import { MultiTextMessageDialogComponent } from './common/multi-text-message-dialog/multi-text-message-dialog.component';
import { MultiTextMessagePipe } from './common/pipes/multi-text-message.pipe';
import { PreviewPipe } from './common/pipes/preview.pipe';
import { TypeOfTransBaseLangPipe } from './common/pipes/type-of-trans-base-lang.pipe';
import { ValidityPeriodPipe } from './common/pipes/validity-period.pipe';
import { HtmlPipe } from './common/pipes/html.pipe';
import { CsvService } from './common/csv/csv.service';

import { ManualPurchaseContentDailyComponent } from './manuals/manual-purchase-content-daily/manual-purchase-content-daily.component';
import { ManualSelectPurchaseContentDailyDialogComponent } from './manuals/manual-select-purchase-content-daily-dialog/manual-select-purchase-content-daily-dialog.component';
import { ManualEntranceExitEventComponent } from './manuals/manual-entrance-exit-event/manual-entrance-exit-event.component';
import { ManualEntranceExitQuestionComponent } from './manuals/manual-entrance-exit-question/manual-entrance-exit-question.component';
import { ManualEntranceExitQuestionDialogComponent } from './manuals/manual-entrance-exit-question-dialog/manual-entrance-exit-question-dialog.component';
import { ManualEntranceExitEntryComponent } from './manuals/manual-entrance-exit-entry/manual-entrance-exit-entry.component';
import { ManualEntranceExitEntryDialogComponent } from './manuals/manual-entrance-exit-entry-dialog/manual-entrance-exit-entry-dialog.component';
import { ManualEntranceExitTransitDailyComponent } from './manuals/manual-entrance-exit-transit-daily/manual-entrance-exit-transit-daily.component';
import { ManualEntranceExitTransitDailyDialogComponent } from './manuals/manual-entrance-exit-transit-daily-dialog/manual-entrance-exit-transit-daily-dialog.component';
import { ManualEntranceExitExhibitTransitDownloadComponent } from './manuals/manual-entrance-exit-exhibit-transit-download/manual-entrance-exit-exhibit-transit-download.component';
import { ManualEntranceExitExhibitTransitDownloadDialogComponent } from './manuals/manual-entrance-exit-exhibit-transit-download-dialog/manual-entrance-exit-exhibit-transit-download-dialog.component';
import { ManualSplatsComponent } from './manuals/manual-splats/manual-splats.component';
import { ManualEntranceExitUserComponent } from './manuals/manual-entrance-exit-user/manual-entrance-exit-user.component';
import { ManualEntranceExitUserDialogComponent } from './manuals/manual-entrance-exit-user-dialog/manual-entrance-exit-user-dialog.component';
import { ManualSelectEntranceExitEntryDialogComponent } from './manuals/manual-select-entrance-exit-entry-dialog/manual-select-entrance-exit-entry-dialog.component';
import { ManualSelectEntranceExitTransitDailyDialogComponent } from './manuals/manual-select-entrance-exit-transit-daily-dialog/manual-select-entrance-exit-transit-daily-dialog.component';
import { ManualEntranceExitTransitDialogComponent } from './manuals/manual-entrance-exit-transit-dialog/manual-entrance-exit-transit-dialog.component';


export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: environment.apiRootUrl
  }
  return new Configuration(params);
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    TopComponent,
    TypeOfKeypadPipe,
    HeaderComponent,
    SignInComponent,
    SignUpComponent,
    PasswordResetMailComponent,
    PasswordResetComponent,
    ProjectComponent,
    UserComponent,
    FacilityComponent,
    MenuDispMapRangePipe,
    TypeOfMapPipe,
    UseFunctionPipe,
    ExhibitComponent,
    ExhibitDetailComponent,
    ExhibitRankingsComponent,
    VoiceFileDownloadComponent,
    ReservationFacilityComponent,
    ReservationFacilityDialogComponent,
    ReservationFacilitySpecialComponent,
    ReservationFacilitySpecialDialogComponent,
    ReservationQuestionsComponent,
    ReservationQuestionDialogComponent,
    SelectReservationQuestionsDialogComponent,
    ReservationFacilityReservationDialogComponent,
    ReservationComponent,
    ReservationDialogComponent,
    SelectReservationDialogComponent,
    SelectReservationReservationFacilitiesDialogComponent,
    ConfirmReservationDialogComponent,
    ConfirmReservationDetailDialogComponent,
    ReservationDownloadComponent,
    ReservationDownloadDialogComponent,
    FacilityUserComponent,
    FacilityUserDialogComponent,
    SelectFacilitiesDialogComponent,
    PurchaseContentDailyComponent,
    SelectPurchaseContentDailyDialogComponent,
    SelectHallsDialogComponent,
    EntranceExitUserComponent,
    EntranceExitUserDialogComponent,
    EntranceExitEventComponent,
    SelectEntranceExitQuestionsDialogComponent,
    SelectEntranceExitEventFloorsDialogComponent,
    EntranceExitQuestionComponent,
    EntranceExitQuestionDialogComponent,
    EntranceExitEntryComponent,
    EntranceExitEntryDialogComponent,
    SelectEntranceExitEntryDialogComponent,
    EntranceExitTransitDialogComponent,
    EntranceExitTransitDailyComponent,
    EntranceExitTransitDailyDialogComponent,
    SelectEntranceExitTransitDailyDialogComponent,
    EntranceExitExhibitTransitDownloadComponent,
    EntranceExitExhibitTransitDownloadDialogComponent,
    SplatsComponent,
    NumberDayPipe,
    ReservationStatusPipe,
    MailSendStatusPipe,
    GenderPipe,
    ExhibitDialogComponent,
    ExhibitDetailDialogComponent,
    FacilityDialogComponent,
    FacilityAttributeComponent,
    FacilityAttributeDialogComponent,
    ExhibitAttributeComponent,
    ExhibitAttributeDialogComponent,
    ExhibitDetailAttributeComponent,
    ExhibitDetailAttributeDialogComponent,
    ReservationFacilityAttributeComponent,
    ReservationFacilityAttributeDialogComponent,
    UploadedFilesAttributeComponent,
    UploadedFilesAttributeDialogComponent,
    ProjectDialogComponent,
    DropzoneDirective,
    UploaderComponent,
    UploaderDLComponent,
    UploadTaskComponent,
    SelectUploadedFileDialogComponent,
    SelectExhibitsDialogComponent,
    SelectExhibitDetailsDialogComponent,
    SelectReservationFacilitiesDialogComponent,
    MessageDialogComponent,
    UserDialogComponent,
    TopDialogComponent,
    MultiNameDialogComponent,
    SelectLanguagesDialogComponent,
    SelectLogoFileDialogComponent,
    DocPipe,
    TypeOfUserPipe,
    MultiNamePipe,
    MultiDescDialogComponent,
    MultiDescPipe,
    MultiAddressDialogComponent,
    MultiAddressPipe,
    MultiInquiryDialogComponent,
    MultiPolicyDialogComponent,
    MultiCopyrightDialogComponent,
    MultiFaqDialogComponent,
    MultiInquiryPipe,
    MultiPolicyPipe,
    MultiCopyrightPipe,
    MultiFaqPipe,
    FloorComponent,
    FloorDialogComponent,
    SelectFloorsDialogComponent,
    MultiFloorDialogComponent,
    HallComponent,
    HallDialogComponent,
    TypeOfMenuPipe,
    TypeOfVoiceControlPipe,
    TypeOfPinTapVoiceControlPipe,
    DocsByCondPipe,
    GeneralTextComponent,
    GeneralTextDialogComponent,
    GeneralTextPipe,
    MultiGeneralTextDialogComponent,
    LanguageComponent,
    LanguageDialogComponent,
    SelectLangFileDialogComponent,
    SelectVoiceFileDialogComponent,
    SelectFaviconFileDialogComponent,
    SelectAngularIconDialogComponent,
    ReflectDataDialogComponent,
    NoticeComponent,
    NoticeManagementComponent,
    NoticeManagementDialogComponent,
    SelectProjectDialogComponent,
    MultiAppnameDialogComponent,
    ManualComponent,
    ManualIndexComponent,
    ManualTopComponent,
    ManualTopDialogComponent,
    ManualProjectComponent,
    ManualProjectDialogComponent,
    ManualUserComponent,
    ManualUserDialogComponent,
    ManualUploaderComponent,
    ManualUploaderDLComponent,
    ManualFacilityComponent,
    ManualFacilityDialogComponent,
    ManualHallComponent,
    ManualHallDialogComponent,
    ManualFloorComponent,
    ManualFloorDialogComponent,
    ManualExhibitComponent,
    ManualExhibitDialogComponent,
    ManualExhibitDetailComponent,
    ManualExhibitDetailDialogComponent,
    ManualReservationComponent,
    ManualReservationQuestionComponent,
    ManualReservationQuestionDialogComponent,
    ManualReservationDownloadComponent,
    ManualReservationDownloadDialogComponent,
    ManualReservationDialogComponent,
    ManualReservationFacilityComponent,
    ManualReservationFacilityDialogComponent,
    ManualReservationFacilityReservationComponent,
    ManualReservationFacilitySpecialComponent,
    ManualReservationFacilitySpecialDialogComponent,
    ManualUserInputTitleDialogComponent,
    ManualSendMailTitleDialogComponent,
    ManualSendMailMessageDialogComponent,
    ManualFacilityAttributeComponent,
    ManualFacilityAttributeDialogComponent,
    ManualExhibitAttributeComponent,
    ManualExhibitAttributeDialogComponent,
    ManualExhibitDetailAttributeComponent,
    ManualExhibitDetailAttributeDialogComponent,
    ManualReservationFacilityAttributeComponent,
    ManualReservationFacilityAttributeDialogComponent,
    ManualNoticeComponent,
    ManualReflectDataComponent,
    ManualAppnameDialogComponent,
    ManualNameDialogComponent,
    ManualDescDialogComponent,
    ManualAddressDialogComponent,
    ManualFaqDialogComponent,
    ManualPolicyDialogComponent,
    ManualCopyrightDialogComponent,
    ManualSelectReservationDialogComponent,
    ManualConfirmReservationDialogComponent,
    ManualConfirmReservationDetailDialogComponent,
    ManualVoiceFileDownloadComponent,
    ProgressSpinnerComponent,
    MultiYoutubeDialogComponent,
    MultiExternallinkDialogComponent,
    MultiCodePipe,
    ManualYoutubeDialogComponent,
    QrcodeComponent,
    QrcodeResultComponent,
    URLResultComponent,
    SelectedLanguagesDialogComponent,
    ManualQrcodeComponent,
    ManualQrcodeResultComponent,
    ManualURLResultComponent,
    MultiQnameDialogComponent,
    MultiQcompDialogComponent,
    MultiQitemDialogComponent,
    QuestionsComponent,
    QuestionDialogComponent,
    QuestionSortDialogComponent,
    ManualQuestionComponent,
    ManualQuestionDialogComponent,
    ManualQuestionSortDialogComponent,
    ManualQnameDialogComponent,
    ManualQcompDialogComponent,
    ManualQitemDialogComponent,
    EnqueteComponent,
    EnqueteDialogComponent,
    ManualEnqueteComponent,
    ManualEnqueteDialogComponent,
    MultiQnamePipe,
    MultiQcompPipe,
    MultiQitemPipe,
    ManualUrlDialogComponent,
    ManualExhibitRankingComponent,
    BoardThreadComponent,
    BoardCommentComponent,
    BoardThreadDialogComponent,
    BoardCommentDialogComponent,
    CutterPipe,
    ManualBoardThreadComponent,
    ManualBoardThreadDialogComponent,
    ManualBoardCommentComponent,
    ManualBoardCommentDialogComponent,
    SheltersComponent,
    ShelterDialogComponent,
    ManualShelterComponent,
    ManualShelterDialogComponent,
    QuestSetComponent,
    QuestSetDialogComponent,
    SelectQuestionsDialogComponent,
    ManualQuestSetComponent,
    ManualQuestSetDialogComponent,
    MethodOfReservationPipe,
    MethodOfLotteryPipe,
    UnitOfReservationPipe,
    MultiRitemDialogComponent,
    MultiRitemPipe,
    TypeOfReservationSpecialDatePipe,
    MultiUserInputTitleDialogComponent,
    MultiUserInputTitlePipe,
    MultiSendMailTitleDialogComponent,
    MultiSendMailTitlePipe,
    MultiSendMailMessageDialogComponent,
    MultiSendMailMessagePipe,
    MultiMailTitleDialogComponent,
    MultiMailTitlePipe,
    MultiMailMessageDialogComponent,
    MultiMailMessagePipe,
    MultiStringMessageDialogComponent,
    MultiStringMessagePipe,
    MultiTextMessageDialogComponent,
    MultiTextMessagePipe,
    PreviewPipe,
    TypeOfTransBaseLangPipe,
    ValidityPeriodPipe,
    HtmlPipe,
    ManualFacilityUserComponent,
    ManualFacilityUserDialogComponent,
    ManualPurchaseContentDailyComponent,
    ManualSelectPurchaseContentDailyDialogComponent,
    ManualEntranceExitEventComponent,
    ManualEntranceExitQuestionComponent,
    ManualEntranceExitQuestionDialogComponent,
    ManualEntranceExitUserComponent,
    ManualEntranceExitUserDialogComponent,
    ManualEntranceExitEntryComponent,
    ManualSelectEntranceExitEntryDialogComponent,
    ManualEntranceExitEntryDialogComponent,
    ManualEntranceExitTransitDialogComponent,
    ManualEntranceExitTransitDailyComponent,
    ManualSelectEntranceExitTransitDailyDialogComponent,
    ManualEntranceExitTransitDailyDialogComponent,
    ManualEntranceExitExhibitTransitDownloadComponent,
    ManualEntranceExitExhibitTransitDownloadDialogComponent,
    ManualSplatsComponent,
  ],
  imports: [
    BrowserModule,
    // HttpClientModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HammerModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    MaterialModule,
    DragDropModule,
    GoogleMapsModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule,
    AngularFireStorageModule,
    AngularFireAuthModule,
    AngularFireAuthGuardModule,
    SocialLoginModule,
    ApiModule.forRoot(apiConfigFactory),
    ToastrModule.forRoot({
      closeButton: true,
      timeOut: 2000,
      positionClass: 'toast-top-full-width'
    }),
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatNativeDateModule,
    NgxMatMomentModule,
    QRCodeModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    })
    // TranslateModule.forRoot()

  ],
  // entryComponents: [
  //   TopDialogComponent,
  //   ProjectDialogComponent,
  //   FacilityDialogComponent,
  //   ExhibitDialogComponent,
  //   SelectUploadedFileDialogComponent,
  //   SelectExhibitsDialogComponent,
  //   SelectReservationFacilitiesDialogComponent,
  //   MessageDialogComponent,
  //   SelectLanguagesDialogComponent,
  //   SelectedLanguagesDialogComponent
  // ],
  providers: [
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              environment.googleAuthClientId
            )
          },
        ],
        onError: (err: any) => {
          console.error(err);
        }
      } as SocialAuthServiceConfig,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'ja-JP' },
    { provide: NGX_MAT_DATE_FORMATS, useValue: APP_DATETIME_FORMATS },
    { provide: NgxMatDateAdapter, useClass: AppDatetimeAdapter, deps: [MAT_DATE_LOCALE, NGX_MAT_DATE_FORMATS] },
    provideHttpClient(withInterceptorsFromDi()),
    CsvService,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

